import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { MARKETING_ROUTES } from "@/marketing/routes";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/marketing/components/ui/button";
import { useAuthStore } from "@/marketing/stores/useAuthStore";
import {
  type InfluencerCampaign,
  type InfluencerCampaignFilter,
  InfluencerCampaignStatus,
  InfluencerCampaignTrafficLight,
} from "@/marketing/models/influencerCampaign";
import { influencerCampaignAPIClient } from "@/marketing/infrastructure/influencer/campaign/influencerCampaignAPIClient";
import { INFLUENCER_CAMPAIGN_KEYS } from "@/marketing/infrastructure/influencer/campaign/utils/keys";
import { INFLUENCER_CAMPAIGN_QUERIES } from "@/marketing/infrastructure/influencer/campaign/utils/queries";
import { BRAND_QUERIES } from "@/marketing/infrastructure/influencer/brand/utils/queries";

import { CampaignKpiCards } from "./CampaignKpiCards";
import { CampaignFilterBar } from "./CampaignFilterBar";
import { CampaignBoardView } from "./CampaignBoardView";
import { CampaignTableView } from "./CampaignTableView";
import { CampaignDetailSheet } from "./CampaignDetailSheet";
import { NewCampaignDialog } from "./NewCampaignDialog";
import { applyCampaignOrder } from "./campaignOrder";
import { CampaignTrackerView } from "./tracker/CampaignTrackerView";

type ViewMode = "board" | "table" | "tracker";

function EmptyOrLoading({ text }: { text: string }) {
  return (
    <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-sm tw-text-muted-foreground">
      {text}
    </div>
  );
}

export function CampaignManagerPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);

  const handleOpenCampaign = (campaign: InfluencerCampaign) => {
    navigate(MARKETING_ROUTES.campaignDetail(campaign.campaignId));
  };

  const [search, setSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState<"me" | "all">("all");
  const [brandFilter, setBrandFilter] = useState<string>(""); // stores brandId
  const [trafficFilter, setTrafficFilter] = useState<
    "all" | "red" | "yellow" | "green"
  >("all");
  const [view, setView] = useState<ViewMode>("board");
  const [completedCollapsed, setCompletedCollapsed] = useState(true);
  const [selected, setSelected] = useState<InfluencerCampaign | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] =
    useState<InfluencerCampaign | null>(null);

  const apiFilter = useMemo<InfluencerCampaignFilter>(() => {
    const filter: InfluencerCampaignFilter = {};
    if (ownerFilter === "me" && currentUser?.email)
      filter.ownerId = currentUser.email;
    if (brandFilter) filter.brandId = brandFilter;
    return filter;
  }, [ownerFilter, brandFilter, currentUser?.email]);

  const { data: campaigns = [], isLoading } = useQuery(
    INFLUENCER_CAMPAIGN_QUERIES.list(apiFilter),
  );

  const { data: brandList = [] } = useQuery(BRAND_QUERIES.list({ limit: 100 }));

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (term && !c.name.toLowerCase().includes(term)) {
        return false;
      }
      if (trafficFilter !== "all") {
        const tl = c.trafficLight;
        if (
          trafficFilter === "red" &&
          tl !== InfluencerCampaignTrafficLight.Red
        )
          return false;
        if (
          trafficFilter === "yellow" &&
          tl !== InfluencerCampaignTrafficLight.Yellow
        )
          return false;
        if (
          trafficFilter === "green" &&
          tl !== InfluencerCampaignTrafficLight.Green
        )
          return false;
      }
      return true;
    });
  }, [campaigns, search, trafficFilter]);

  const grouped = useMemo(() => {
    const map: Record<InfluencerCampaignStatus, InfluencerCampaign[]> = {
      [InfluencerCampaignStatus.NotStarted]: [],
      [InfluencerCampaignStatus.Backlog]: [],
      [InfluencerCampaignStatus.InProgress]: [],
      [InfluencerCampaignStatus.Completed]: [],
    };
    for (const campaign of filtered) {
      if (map[campaign.status]) map[campaign.status].push(campaign);
    }
    return map;
  }, [filtered]);

  const brandOptions = useMemo(
    () => brandList.map((b) => ({ brandId: b.brandId, name: b.name })),
    [brandList],
  );

  const kpi = useMemo(() => {
    const inProgress = grouped[InfluencerCampaignStatus.InProgress].length;
    let onboardingTarget = 0;
    let postTarget = 0;
    let dueThisWeek = 0;
    const now = Date.now();
    const weekFromNow = now + 7 * 24 * 60 * 60 * 1000;
    for (const c of filtered) {
      onboardingTarget += c.onboardingTarget ?? 0;
      postTarget += c.postTarget ?? 0;
      if (c.postingDueDate) {
        const ts = new Date(c.postingDueDate).getTime();
        if (!Number.isNaN(ts) && ts >= now && ts <= weekFromNow)
          dueThisWeek += 1;
      }
    }
    return { inProgress, onboardingTarget, postTarget, dueThisWeek };
  }, [filtered, grouped]);

  const handleStatusChange = async (status: InfluencerCampaignStatus) => {
    if (!selected) return;
    try {
      await influencerCampaignAPIClient.updateInfluencerCampaignStatus(
        selected.campaignId,
        {
          status,
        },
      );
      queryClient.invalidateQueries({
        queryKey: INFLUENCER_CAMPAIGN_KEYS.lists(),
      });
      setSelected({ ...selected, status });
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleCardDrop = async (
    campaignId: string,
    status: InfluencerCampaignStatus,
  ) => {
    const current = campaigns.find((c) => c.campaignId === campaignId);
    if (!current || current.status === status) return;
    // A status change refetches the list, which would discard an arrangement that is still only
    // local — save it first so both survive.
    await handleReorderCommit();
    try {
      await influencerCampaignAPIClient.updateInfluencerCampaignStatus(
        campaignId,
        { status },
      );
      queryClient.invalidateQueries({
        queryKey: INFLUENCER_CAMPAIGN_KEYS.lists(),
      });
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  // Positions come from the campaign's index in the column, so a filtered column would number the
  // visible few as if they were the whole list and reshuffle everything hidden from it.
  const isReorderEnabled =
    !search.trim() &&
    trafficFilter === "all" &&
    ownerFilter === "all" &&
    !brandFilter;

  // A sorting session stays local until it is committed. Saving per drag raced itself: each drag
  // sends the whole order, so overlapping requests landed out of order and the refetch then
  // overwrote the arrangement with a stale one.
  const pendingOrderRef = useRef<string[] | null>(null);
  const orderBeforeSessionRef = useRef<InfluencerCampaign[] | undefined>(
    undefined,
  );

  const handleReorder = (orderedCampaignIds: string[]) => {
    const listKey = INFLUENCER_CAMPAIGN_KEYS.list(apiFilter);
    if (!pendingOrderRef.current) {
      orderBeforeSessionRef.current =
        queryClient.getQueryData<InfluencerCampaign[]>(listKey);
    }
    pendingOrderRef.current = orderedCampaignIds;
    queryClient.setQueryData<InfluencerCampaign[]>(listKey, (current) =>
      current ? applyCampaignOrder(current, orderedCampaignIds) : current,
    );
  };

  const handleReorderCommit = async () => {
    const orderedCampaignIds = pendingOrderRef.current;
    if (!orderedCampaignIds) return;
    const campaignsBeforeSession = orderBeforeSessionRef.current;
    pendingOrderRef.current = null;
    orderBeforeSessionRef.current = undefined;
    try {
      await influencerCampaignAPIClient.reorderInfluencerCampaigns(
        orderedCampaignIds,
      );
      toast.success("Campaign order updated");
    } catch {
      queryClient.setQueryData(
        INFLUENCER_CAMPAIGN_KEYS.list(apiFilter),
        campaignsBeforeSession,
      );
      toast.error("Failed to update campaign order");
    } finally {
      queryClient.invalidateQueries({
        queryKey: INFLUENCER_CAMPAIGN_KEYS.lists(),
      });
    }
  };

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-gap-4 tw-overflow-hidden tw-p-6">
      {/* Page header */}
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
        <h1 className="tw-text-2xl tw-font-semibold tw-leading-tight">
          Campaign Manager
        </h1>
        <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2">
          <Button
            size="sm"
            className="tw-gap-1.5"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="tw-h-3.5 tw-w-3.5" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <CampaignKpiCards
        inProgress={kpi.inProgress}
        onboardingTarget={kpi.onboardingTarget}
        postTarget={kpi.postTarget}
        dueThisWeek={kpi.dueThisWeek}
      />

      {/* Filter bar */}
      <CampaignFilterBar
        search={search}
        onSearchChange={setSearch}
        ownerFilter={ownerFilter}
        onOwnerFilterChange={setOwnerFilter}
        currentUserName={currentUser?.name}
        brandFilter={brandFilter}
        onBrandFilterChange={setBrandFilter}
        brandOptions={brandOptions}
        trafficFilter={trafficFilter}
        onTrafficFilterChange={setTrafficFilter}
        view={view}
        onViewChange={setView}
      />

      {/* Body */}
      <div className="tw-min-h-0 tw-flex-1">
        {view === "tracker" ? (
          <CampaignTrackerView onOpenCampaign={handleOpenCampaign} />
        ) : isLoading ? (
          <EmptyOrLoading text="Loading campaigns…" />
        ) : filtered.length === 0 ? (
          <EmptyOrLoading text="No campaigns yet. Click + New Campaign to create one." />
        ) : view === "board" ? (
          <CampaignBoardView
            grouped={grouped}
            completedCollapsed={completedCollapsed}
            isReorderEnabled={isReorderEnabled}
            onToggleCompleted={() => setCompletedCollapsed((v) => !v)}
            onCardClick={handleOpenCampaign}
            onCardEdit={setEditingCampaign}
            onCardDrop={handleCardDrop}
            onReorder={handleReorder}
            onReorderCommit={handleReorderCommit}
          />
        ) : (
          <CampaignTableView
            campaigns={filtered}
            onRowClick={handleOpenCampaign}
            onRowEdit={setEditingCampaign}
          />
        )}
      </div>

      {/* Detail sheet */}
      <CampaignDetailSheet
        selected={selected}
        onClose={() => setSelected(null)}
        onStatusChange={handleStatusChange}
        onEdit={() => {
          if (selected) setEditingCampaign(selected);
          setSelected(null);
        }}
      />

      {/* New / Edit campaign dialog */}
      <NewCampaignDialog
        open={createOpen || !!editingCampaign}
        mode={editingCampaign ? "edit" : "create"}
        initialCampaign={editingCampaign ?? undefined}
        onOpenChange={(open) => {
          if (!open) {
            setCreateOpen(false);
            setEditingCampaign(null);
          }
        }}
        defaultOwnerId={currentUser?.email ?? ""}
      />
    </div>
  );
}
